import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../stores/useStore';
import api from '../services/api';
import { Play, Radar, Users, Settings, LogOut, Activity, Building2, BookOpen, BarChart3, Image, ScrollText } from 'lucide-react';
import BotDamagePanel from '../components/BotDamagePanel';
import GalleryModal from '../components/admin/GalleryModal';
import dashboardHeroImage from '../assets/inicio.jpg';
import { loadCityContext } from './city/cityContextCache';
import { hasMinimumDepartureLoadout } from './city/cityUtils';

const ACTIONS = [
  { key: 'play', title: 'Desplegar', subtitle: 'Entrar al teatro de operaciones', icon: Play, to: '/game', border: 'border-cyan-400/50' },
  { key: 'city', title: 'Ciudad', subtitle: 'Servicios urbanos y gestion', icon: Building2, to: '/city', border: 'border-emerald-400/50' },
  { key: 'tutorial', title: 'Tutorial', subtitle: 'Guia completa de sistemas activos', icon: BookOpen, to: '/tutorial', border: 'border-amber-400/50' },
  { key: 'missions', title: 'Misiones', subtitle: 'Seguimiento de campanas y fases', icon: ScrollText, to: '/missions', border: 'border-emerald-300/40' },
  { key: 'world', title: 'Mapa Mundial', subtitle: 'Control territorial y rutas', icon: Radar, disabled: true },
  { key: 'clan', title: 'Clan/Subclan', subtitle: 'Solicitudes, jerarquia y subclanes', icon: Users, to: '/clan' },
  { key: 'battleStats', title: 'Estadisticas de batalla', subtitle: 'Analitica tactica del bot', icon: BarChart3, to: '/battle-stats', border: 'border-cyan-400/50' }
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, robot, setRobot } = useStore();
  const apiBase = import.meta.env.VITE_API_URL || '';

  const [loading, setLoading] = useState(true);
  const [gateError, setGateError] = useState('');
  const [motd, setMotd] = useState('Bienvenido, piloto.');
  const [heroImageSrc, setHeroImageSrc] = useState(dashboardHeroImage);
  const [sectorName, setSectorName] = useState('');
  const [cityName, setCityName] = useState('');
  const [cityContext, setCityContext] = useState(null);
  const [checkingDeparture, setCheckingDeparture] = useState(false);
  const [departureWarning, setDepartureWarning] = useState(null);
  const [showBannerGallery, setShowBannerGallery] = useState(false);
  const [savingHeroImage, setSavingHeroImage] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [missionsOverview, setMissionsOverview] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      try {
        const [onboardingRes, botRes] = await Promise.all([
          api.get('/onboarding/state'),
          api.get('/robots/my-bot').catch(() => null)
        ]);

        const onboarding = onboardingRes.data?.onboarding;
        if (!onboarding?.completed) {
          navigate('/onboarding', { replace: true });
          return;
        }

        if (!botRes?.data?.robot) {
          navigate('/onboarding', { replace: true });
          return;
        }

        if (mounted) {
          const currentBot = botRes.data.robot;
          setRobot(currentBot);
          const publicSettings = await api.get('/public-settings').catch(() => null);
          const publicSettingsObj = publicSettings?.data?.settings || {};
          if (publicSettingsObj.motd) {
            setMotd(publicSettingsObj.motd);
          }
          if (publicSettingsObj.dashboard_hero_image) {
            setHeroImageSrc(`${apiBase}/uploads/gallery/${publicSettingsObj.dashboard_hero_image}`);
          }
          if (currentBot?.id_sector) {
            const sectorRes = await api.get(`/maps/${currentBot.id_sector}`).catch(() => null);
            if (sectorRes?.data?.sector?.name) {
              setSectorName(sectorRes.data.sector.name);
            }
          }
          const cityContext = await loadCityContext().catch(() => null);
          setCityContext(cityContext);
          if (cityContext?.city?.name) {
            setCityName(cityContext.city.name);
          } else {
            setCityName('');
          }
          const missionsRes = await api.get('/missions/overview').catch(() => null);
          if (missionsRes?.data) {
            setMissionsOverview(missionsRes.data);
          }
        }
      } catch (err) {
        if (mounted) {
          setGateError(err.response?.data?.error || 'No se pudo inicializar el panel');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      mounted = false;
    };
  }, [apiBase, navigate, setRobot]);

  async function handleBannerImageSelect(filename) {
    try {
      setSavingHeroImage(true);
      setBannerError('');
      await api.post('/admin/settings', { dashboard_hero_image: filename });
      setHeroImageSrc(`${apiBase}/uploads/gallery/${filename}`);
      setShowBannerGallery(false);
    } catch (err) {
      setBannerError(err.response?.data?.error || 'No se pudo actualizar la imagen del banner');
    } finally {
      setSavingHeroImage(false);
    }
  }


  async function handlePlayClick() {
    if (checkingDeparture) return;

    try {
      setCheckingDeparture(true);
      setDepartureWarning(null);

      const nextCityContext = await loadCityContext({ force: true }).catch(() => null);
      setCityContext(nextCityContext);
      if (nextCityContext?.transport) {
        navigate('/city/transport');
        return;
      }

      if (!nextCityContext?.city) {
        navigate('/game');
        return;
      }

      const inventoryRes = await api.get('/robots/my-inventory');
      const readiness = hasMinimumDepartureLoadout(inventoryRes.data);

      if (!readiness.canLeave) {
        setDepartureWarning({
          cityName: cityContext.city?.name || 'Ciudad',
          missing: readiness.missing,
          summary: readiness
        });
        return;
      }

      navigate('/game');
    } catch (err) {
      setDepartureWarning({
        cityName: cityName || 'Ciudad',
        missing: ['No se pudo verificar el equipo mínimo'],
        summary: null,
        error: err.response?.data?.error || 'No se pudo verificar el equipo mínimo para desplegar'
      });
    } finally {
      setCheckingDeparture(false);
    }
  }

  const transportActive = Boolean(cityContext?.transport);
  const visibleActions = transportActive
    ? ACTIONS.filter((action) => action.key === 'tutorial' || action.key === 'missions')
    : ACTIONS;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">
        Sincronizando consola tactica...
      </div>
    );
  }

  if (gateError) {
    return (
      <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">
        {gateError}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#04060d] text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 shadow-[0_0_40px_rgba(0,255,255,0.12)]">
          <img
            src={heroImageSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            draggable="false"
          />
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(4,9,18,0.72),rgba(6,10,20,0.66))]" />

          <div className="relative z-10 flex flex-wrap items-start justify-between gap-4 p-5">
            <div>
              <h1 className="text-3xl font-bold tracking-wide text-cyan-300">TERRANWAR COMMAND CONSOLE</h1>
              <p className="text-cyan-100/70 text-sm mt-1">Piloto: {user?.username || 'Unknown'} | Estado: En linea</p>
              <p className="text-slate-300/80 text-sm mt-3">{motd}</p>
            </div>
            <div className="flex items-center gap-3">
              {user?.isAdmin && (
                <>
                  <button
                    onClick={() => setShowBannerGallery(true)}
                    className="px-3 py-2 rounded-lg border border-cyan-400/50 bg-cyan-500/10 hover:bg-cyan-500/20 flex items-center gap-2 text-cyan-200"
                    disabled={savingHeroImage}
                  >
                    <Image size={16} /> Banner
                  </button>
                  <button
                    onClick={() => navigate('/admin')}
                    className="px-3 py-2 rounded-lg border border-fuchsia-400/50 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 flex items-center gap-2 text-fuchsia-200"
                  >
                    <Settings size={16} /> Master
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  useStore.getState().logout();
                  navigate('/login');
                }}
                className="px-3 py-2 rounded-lg border border-rose-400/50 bg-rose-500/10 hover:bg-rose-500/20 flex items-center gap-2 text-rose-200"
              >
                <LogOut size={16} /> Salir
              </button>
            </div>
          </div>
          {bannerError && (
            <div className="relative z-10 px-5 pb-4">
              <div className="inline-flex items-center rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                {bannerError}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 mt-5">
          <section className="space-y-4">
            {transportActive && (
              <section
                className="relative overflow-hidden rounded-2xl border border-red-400/40 bg-[#05070f] shadow-[0_0_40px_rgba(255,40,40,0.18)]"
                style={{
                  backgroundImage: 'url(/img/air-travel-banner.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-[#01040a]/88" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#01040a]/96 via-[#01040a]/82 to-[#01040a]/92" />
                <div className="relative z-10 p-5 md:p-6 flex flex-col gap-4">
                  <div className="inline-flex items-center gap-2 rounded-full border border-red-200/50 bg-red-950/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.35em] text-red-50 w-fit">
                    Bloqueo aéreo activo
                  </div>
                  <div className="max-w-3xl">
                    <h2 className="text-3xl md:text-4xl font-black tracking-wide text-[#fff1f1] drop-shadow-[0_0_14px_rgba(0,0,0,0.85)]">
                      El BOT está en tránsito
                    </h2>
                    <p className="mt-2 text-sm md:text-base text-slate-100 max-w-2xl drop-shadow-[0_0_10px_rgba(0,0,0,0.9)]">
                      Mientras dure el vuelo no puedes entrar al mapa, inventario ni clan. Solo el módulo de transporte está disponible hasta aterrizar.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/city/transport')}
                      className="rounded-xl border border-red-200/40 bg-black/45 px-4 py-3 text-sm font-bold text-red-50 hover:bg-black/65"
                    >
                      Abrir transporte
                    </button>
                  </div>
                </div>
              </section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visibleActions.map((action) => {
                const Icon = action.icon;
                const disabled = action.disabled || (action.key === 'play' && checkingDeparture);
                const cardClass = action.disabled
                  ? 'opacity-60 cursor-not-allowed border-slate-700 bg-[#0a1020]'
                  : disabled
                    ? 'opacity-70 cursor-wait border-cyan-400/40 bg-[#091427]'
                  : `${action.border} bg-[#091427] hover:border-cyan-300`;
                const dynamicSubtitle = action.key === 'city'
                  ? (cityName ? cityName : action.subtitle)
                  : action.key === 'missions'
                    ? (missionsOverview?.activeMission
                      ? `Progreso registrado: ${missionsOverview.activeMission.completedEventsCount || 0}/${missionsOverview.activeMission.totalEventsCount || 0}`
                      : `Finalizadas: ${missionsOverview?.completedMissions?.length || 0}`)
                  : action.subtitle;
                return (
                  <button
                    key={action.key}
                    onClick={() => {
                      if (action.disabled || !action.to) return;
                      if (action.key === 'play') {
                        void handlePlayClick();
                        return;
                      }
                      navigate(action.to);
                    }}
                    disabled={disabled}
                    className={`text-left rounded-xl border p-4 transition-all ${cardClass}`}
                  >
                    <Icon size={22} className="text-cyan-300 mb-2" />
                    <h3 className="font-bold text-cyan-200">{action.title}</h3>
                    <p className="text-sm text-slate-300/80">{dynamicSubtitle}</p>
                    {action.key === 'play' && checkingDeparture && (
                      <p className="text-xs text-cyan-300 mt-2">Comprobando equipo mínimo...</p>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
              <h2 className="font-bold text-cyan-300 mb-3 flex items-center gap-2">
                <Activity size={18} /> Estado del BOT
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border border-slate-700 p-3 bg-[#060c18]">
                  <div className="text-slate-400">BOT</div>
                  <div className="text-cyan-200 font-bold">{robot?.name || 'Sin nombre'}</div>
                </div>
                <div className="rounded-lg border border-slate-700 p-3 bg-[#060c18]">
                  <div className="text-slate-400">Nivel</div>
                  <div className="text-cyan-200 font-bold">{robot?.level || 1}</div>
                </div>
                <div className="rounded-lg border border-slate-700 p-3 bg-[#060c18]">
                  <div className="text-slate-400">Experiencia</div>
                  <div className="text-cyan-200 font-bold">{robot?.experience || 0}</div>
                </div>
              </div>
              <div className="mt-4">
                {!transportActive && (
                  <button
                    onClick={() => navigate('/bot-inventory')}
                    className="rounded-lg border border-cyan-400/60 bg-cyan-500/10 px-4 py-2 text-cyan-200 hover:bg-cyan-500/20"
                  >
                    Inventario del BOT
                  </button>
                )}
              </div>
            </div>
          </section>

          <aside className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="font-bold text-cyan-300 mb-3">Monitor Tactico</h2>
            <div className="space-y-2 text-sm text-slate-300/85">
              <div className="border border-slate-700 rounded-lg p-3 bg-[#060c18]">
                Sector actual: <span className="text-cyan-200 font-bold">{sectorName || `#${robot?.id_sector ?? 'N/A'}`}</span>
              </div>
              <div className="border border-slate-700 rounded-lg p-3 bg-[#060c18]">
                Coordenada: <span className="text-cyan-200 font-bold">{robot?.coordinate ?? 0}</span>
              </div>
              <div className="border border-slate-700 rounded-lg p-3 bg-[#060c18]">
                Oro: <span className="text-cyan-200 font-bold">{robot?.money ?? 0}</span>
              </div>
            </div>

            <BotDamagePanel
              bot={robot}
              title="Daño del BOT"
              className="mt-4"
            />
          </aside>
        </div>

        {departureWarning && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-3xl border border-amber-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-amber-400/20">
                <p className="text-xs uppercase tracking-[0.25em] text-amber-300/70">Bloqueo de despliegue</p>
                <h3 className="text-2xl font-bold text-white mt-1">{departureWarning.cityName}</h3>
                <p className="text-sm text-slate-300 mt-2">
                  No puedes salir de la ciudad todavía. Debes completar el equipo mínimo antes de desplegar el bot.
                </p>
              </div>

              <div className="p-5 space-y-3 text-sm text-slate-300">
                <p className="text-amber-200">
                  Falta al menos: <span className="font-semibold">{departureWarning.missing.join(', ')}</span>.
                </p>
                <p>
                  Sin arma, munición, mochila o batería activas, el bot no puede salir al mapa de combate.
                </p>
                {departureWarning.error && (
                  <p className="text-rose-300 text-xs">{departureWarning.error}</p>
                )}
              </div>

              <div className="px-5 pb-5 flex gap-3">
                <button
                  type="button"
                  onClick={() => setDepartureWarning(null)}
                  className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDepartureWarning(null);
                    navigate('/city/hangar');
                  }}
                  className="flex-1 rounded-xl border border-cyan-400 bg-cyan-600 px-4 py-3 text-white font-semibold hover:bg-cyan-500"
                >
                  Ir al hangar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      <GalleryModal
        isOpen={showBannerGallery}
        onClose={() => setShowBannerGallery(false)}
        onSelect={(filename) => {
          void handleBannerImageSelect(filename);
        }}
      />
    </div>
  );
}
